import { useAuth } from '../auth/AuthContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Check, ExternalLink, Link2, Maximize2 } from 'lucide-react';
import type {
  ResearchComment,
  ResearchLinkRef,
  ResearchVisibility,
  SourceRef,
} from '@almanac/contracts';
import { RESEARCH_VISIBILITIES } from '@almanac/contracts';
import { MarkdownPrimitive } from '@almanac/catalog';
import { useApi } from '../api/useApi';
import { useMe } from '../api/MeContext';
import type { ResearchDetail } from '../api/client';
import { markResearchSeen } from '../lib/research-seen';
import { useAssistant } from '../components/assistant/AssistantProvider';
import { askAlmanacAssistant } from '../lib/ask-assistant';
import { useCopyToClipboard } from '../lib/copy';
import { formatTimestampDate } from '../lib/format';
import {
  MIN_POSTER_HEIGHT,
  framedPosterHeight,
  POSTER_HEIGHT_MESSAGE,
  POSTER_SANDBOX,
  POSTER_SCROLL_MODE_MESSAGE,
  usePosterColorScheme,
  withPosterMeasureScript,
} from '../lib/poster';

interface ResearchData {
  item: ResearchDetail;
  comments: ResearchComment[];
  derived_from: ResearchLinkRef[];
  findings: ResearchLinkRef[];
}

/** Only surface a source as a link when it carries a safe http(s) URL. */
function httpHref(source: SourceRef): string | null {
  const url = source.url;
  if (!url) return null;
  return /^https?:\/\//iu.test(url) ? url : null;
}

/** Full research-item view: title, meta, markdown body, sources, and comments. */
export function ResearchItemPage() {
  const { researchId = '' } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { status: authStatus, sessionVersion } = useAuth();
  const assistantOpen = useAssistant().open;
  const [data, setData] = useState<ResearchData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const viewerSub = useMe()?.sub ?? null;
  const [sourceConversation, setSourceConversation] = useState<{
    id: string;
    api: typeof api;
    session: number;
    messages: { role: string; text: string }[];
  } | null>(null);
  const [sourceError, setSourceError] = useState<{
    id: string;
    api: typeof api;
    session: number;
    message: string;
  } | null>(null);
  const sourceRequest = useRef(0);
  useEffect(() => {
    sourceRequest.current += 1;
    return () => {
      sourceRequest.current += 1;
    };
  }, [api, researchId, sessionVersion, authStatus]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { copiedKey, copyError, copy } = useCopyToClipboard();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const posterFrameRef = useRef<HTMLDivElement>(null);
  const [posterHeight, setPosterHeight] = useState(MIN_POSTER_HEIGHT);
  const posterColorScheme = usePosterColorScheme();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenSupported] = useState(
    () => typeof document !== 'undefined' && Boolean(document.fullscreenEnabled),
  );

  useEffect(() => {
    setSourceError(null);
    setSourceConversation(null);
    setData(null);
    setError(null);
    setPosterHeight(MIN_POSTER_HEIGHT);
    api
      .getResearch(researchId)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [api, researchId]);

  // Opening an item clears its "New" badge in the library index. The read
  // state is keyed by viewer, so this waits for the shared identity instead of
  // issuing its own `/api/me` that could fail independently. In-place item
  // updates re-run the effect, so the write is guarded to once per viewer/item.
  const markedSeen = useRef<string | null>(null);
  useEffect(() => {
    const target = data?.item;
    if (!viewerSub || !target) return;
    const key = `${viewerSub}:${target.research_id}`;
    if (markedSeen.current === key) return;
    markedSeen.current = key;
    markResearchSeen(viewerSub, target);
  }, [viewerSub, data]);

  // The poster route (outside the app shell) hands off "ask about this
  // research" via ?ask=1 — consume the flag once, then open the assistant.
  // ?details=1 replaces it so a poster-default item stays on this page once
  // it loads instead of redirecting back to the poster.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('ask') !== '1') return;
    const next = new URLSearchParams(searchParams);
    next.delete('ask');
    next.set('details', '1');
    setSearchParams(next, { replace: true });
    askAlmanacAssistant({ researchId });
  }, [researchId, searchParams, setSearchParams]);

  const isOwner = data !== null && viewerSub !== null && data.item.owner_user_id === viewerSub;
  const isArchived = data?.item.status === 'archived';
  // Poster-first items share the full-screen route; others share the text page.
  const publicPath =
    data?.item.default_view === 'poster' && data.item.has_poster
      ? `/p/${data.item.slug}/poster`
      : `/p/${data?.item.slug ?? ''}`;
  const publicUrl = data ? `${window.location.origin}${publicPath}` : '';
  // The native item URL doubles as the reference the MCP `get_research` tool
  // accepts, so pasting it to an agent loads this item. Almanac runs as an
  // installed PWA with no URL bar — a copy button is the only way to get it.
  const referenceUrl = `${window.location.origin}/research/${researchId}`;

  /** Run one mutation with shared busy/error handling, updating the item in place. */
  async function runAction(action: () => Promise<ResearchDetail | null>) {
    setBusy(true);
    setActionError(null);
    try {
      const item = await action();
      if (item) setData((prev) => (prev ? { ...prev, item } : prev));
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function toggleArchived() {
    void runAction(async () => {
      const res = await api.updateResearch(researchId, {
        status: isArchived ? 'published' : 'archived',
      });
      return res.item;
    });
  }

  function changeVisibility(visibility: ResearchVisibility) {
    void runAction(async () => {
      const res = await api.updateResearch(researchId, { visibility });
      return res.item;
    });
  }

  function deleteItem() {
    const confirmed = window.confirm(
      'Permanently delete this research item? This cannot be undone — archive it instead if you might want it back.',
    );
    if (!confirmed) return;
    setBusy(true);
    setActionError(null);
    void (async () => {
      try {
        await api.deleteResearch(researchId);
        navigate('/research');
      } catch (e: unknown) {
        setActionError(e instanceof Error ? e.message : String(e));
        setBusy(false);
      }
    })();
  }

  const posterSrcDoc = useMemo(() => {
    const html = data?.item.body_html;
    if (!html) return null;
    return withPosterMeasureScript(html);
  }, [data]);

  // Listen for the sandboxed poster's height report. The srcdoc runs with a null
  // origin, so we cannot check event.origin — instead we require the message to
  // come from THIS iframe's contentWindow and carry the expected type, and we
  // read ONLY the height (never trust any other field).
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const payload = event.data;
      if (typeof payload !== 'object' || payload === null) return;
      if ((payload as { type?: unknown }).type !== POSTER_HEIGHT_MESSAGE) return;
      const height = framedPosterHeight((payload as { height?: unknown }).height);
      if (height === null) return;
      setPosterHeight(height);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Track fullscreen on the framed poster container. While fullscreen, the
  // measured content height no longer matches the viewport, so the iframe
  // switches to filling the container (`h-full`) and the poster scrolls
  // internally — which requires telling the sandboxed doc to re-enable its own
  // overflow. On exit we restore measured-height mode and re-clip.
  useEffect(() => {
    function onFullscreenChange() {
      const active = document.fullscreenElement === posterFrameRef.current;
      setIsFullscreen(active);
      iframeRef.current?.contentWindow?.postMessage(
        { type: POSTER_SCROLL_MODE_MESSAGE, scroll: active },
        '*',
      );
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  /**
   * Enter fullscreen on the framed poster container. Esc exits (browser
   * default). Browsers reject the request without a fresh user activation
   * (e.g. unfocused window) — fall back to the full-viewport poster route
   * rather than surfacing an uncaught rejection.
   */
  function requestPosterFullscreen() {
    const element = posterFrameRef.current;
    if (!element) return;
    element.requestFullscreen().catch(() => {
      navigate(`/research/${researchId}/poster`);
    });
  }

  // Items published with default_view 'poster' open as their full-screen
  // poster. The poster's back pill links here with ?details=1, and the
  // poster's "ask" handoff (?ask=1) needs this page, so both skip the redirect.
  // `data` still holds the previous item for one render after `researchId`
  // changes, so only the loaded item's own preference counts.
  if (
    data?.item.research_id === researchId &&
    data.item.default_view === 'poster' &&
    data.item.body_html &&
    !searchParams.has('details') &&
    searchParams.get('ask') !== '1'
  ) {
    return <Navigate to={`/research/${researchId}/poster`} replace />;
  }

  return (
    <div>
      <main className="mx-auto max-w-3xl space-y-4 px-4 py-8 sm:px-6">
        <Link to="/research" className="text-sm text-muted-foreground hover:text-foreground">
          ← All research
        </Link>

        {error ? <p className="text-sm text-danger">{error}</p> : null}
        {!data && !error ? (
          <div className="space-y-3" aria-hidden>
            <div className="h-8 w-2/3 animate-pulse rounded bg-muted" />
            <div className="h-40 animate-pulse rounded-xl bg-muted" />
          </div>
        ) : null}

        {data ? (
          <>
            <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
              {data.item.title}
            </h1>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {data.item.published_at ? (
                <span>{formatTimestampDate(data.item.published_at)}</span>
              ) : null}
              {data.item.kind === 'finding' ? (
                <span className="rounded-full border border-accent/30 bg-accent/20 px-2 py-0.5 text-[11px] text-accent-foreground">
                  finding
                </span>
              ) : null}
              {data.item.visibility !== 'internal' ? (
                <span className="rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground">
                  {data.item.visibility}
                </span>
              ) : null}
              {isArchived ? (
                <span className="rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[11px] text-muted-foreground">
                  archived
                </span>
              ) : null}
              {data.item.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground"
                >
                  {tag}
                </span>
              ))}
              <button
                type="button"
                onClick={() => copy('research-id', data.item.research_id)}
                title="Copy research id"
                className="rounded-full border border-border bg-muted/50 px-2 py-0.5 font-mono text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {copiedKey === 'research-id' ? 'Copied' : data.item.research_id}
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => askAlmanacAssistant({ researchId })}
                className="rounded-md border border-primary/40 px-2.5 py-1 font-medium text-primary hover:bg-primary/10"
              >
                Ask about this research
              </button>
              <button
                type="button"
                onClick={() => copy('reference', referenceUrl)}
                title="Copy a link to this research — paste it to an agent to load it"
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                {copiedKey === 'reference' ? <Check size={14} /> : <Link2 size={14} />}
                {copiedKey === 'reference' ? 'Copied' : 'Copy link'}
              </button>
              {isOwner ? (
                <label className="flex items-center gap-1.5 text-muted-foreground">
                  Visibility
                  <select
                    value={data.item.visibility}
                    disabled={busy}
                    onChange={(e) => changeVisibility(e.target.value as ResearchVisibility)}
                    className="rounded-md border border-border bg-card px-2 py-1 text-xs text-foreground"
                  >
                    {RESEARCH_VISIBILITIES.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {data.item.visibility === 'public' ? (
                <button
                  type="button"
                  onClick={() => copy('public', publicUrl)}
                  className="rounded-md border border-border px-2.5 py-1 font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {copiedKey === 'public' ? 'Copied' : 'Copy public link'}
                </button>
              ) : null}
              <button
                type="button"
                onClick={toggleArchived}
                disabled={busy}
                className="rounded-md border border-border px-2.5 py-1 font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                {isArchived ? 'Unarchive' : 'Archive'}
              </button>
              {isOwner ? (
                <button
                  type="button"
                  onClick={deleteItem}
                  disabled={busy}
                  className="rounded-md border border-danger/40 px-2.5 py-1 font-medium text-danger hover:bg-danger/10 disabled:opacity-50"
                >
                  Delete
                </button>
              ) : null}
              {actionError ? <span className="text-danger">{actionError}</span> : null}
              {copyError ? <span className="text-danger">{copyError}</span> : null}
            </div>

            {posterSrcDoc ? (
              <>
                {/* The text body leads: it's the human summary (including the
                    original prompt) for the full poster below. Collapsible but
                    open by default. */}
                {data.item.body_md ? (
                  <details open>
                    <summary className="cursor-pointer text-sm text-muted-foreground">
                      Text version
                    </summary>
                    <div className="mt-2 rounded-xl border border-border bg-card p-4">
                      <MarkdownPrimitive body={data.item.body_md} />
                    </div>
                  </details>
                ) : null}
                {/*
                  Full-bleed breakout: the poster escapes the max-w-3xl reading
                  column and spans the viewport (minus padding, capped) while
                  staying in-flow. The transform must come off while the
                  container is the fullscreen element — the UA gives it the
                  viewport box, and a lingering -translate-x-1/2 would shift it.
                  With the assistant dock open (lg+), the shell is padded right
                  by 26rem, so the breakout width must shrink by the same amount
                  or it hangs off-screen left and under the dock.
                */}
                <div
                  ref={posterFrameRef}
                  className={
                    isFullscreen
                      ? 'flex flex-col overflow-hidden bg-card'
                      : assistantOpen
                        ? 'relative left-1/2 flex w-[min(100vw-2rem,96rem)] -translate-x-1/2 flex-col overflow-hidden rounded-xl border border-border bg-card lg:w-[min(100vw-28rem,96rem)]'
                        : 'relative left-1/2 flex w-[min(100vw-2rem,96rem)] -translate-x-1/2 flex-col overflow-hidden rounded-xl border border-border bg-card'
                  }
                >
                  <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
                    <span className="text-xs font-medium tracking-wider text-muted-foreground uppercase">
                      Poster
                    </span>
                    <div className="flex items-center gap-1">
                      {fullscreenSupported ? (
                        <button
                          type="button"
                          onClick={requestPosterFullscreen}
                          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <Maximize2 size={16} />
                          Fullscreen
                        </button>
                      ) : null}
                      <Link
                        to={`/research/${researchId}/poster`}
                        target="_blank"
                        rel="noopener"
                        className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <ExternalLink size={16} />
                        Open in new tab
                      </Link>
                    </div>
                  </div>
                  {/*
                    SECURITY (load-bearing): render the agent-authored poster in a
                    sandboxed iframe WITHOUT allow-same-origin. The srcdoc document
                    gets a unique opaque origin, so its scripts cannot reach the
                    parent DOM, cookies, or the OAuth tokens in localStorage.
                    NEVER add allow-same-origin. (See web/src/lib/poster.ts.)
                  */}
                  <iframe
                    ref={iframeRef}
                    title={data.item.title}
                    srcDoc={posterSrcDoc}
                    sandbox={POSTER_SANDBOX}
                    className={isFullscreen ? 'block min-h-0 w-full flex-1' : 'block w-full'}
                    style={
                      isFullscreen
                        ? { colorScheme: posterColorScheme }
                        : { height: `${posterHeight}px`, colorScheme: posterColorScheme }
                    }
                  />
                </div>
              </>
            ) : (
              <div className="rounded-xl border border-border bg-card p-4">
                <MarkdownPrimitive body={data.item.body_md} />
              </div>
            )}

            {data.item.sources.length > 0 ? (
              <section className="space-y-2">
                <h2 className="font-display text-lg font-semibold text-foreground">Sources</h2>
                <ul className="space-y-1 text-sm">
                  {data.item.sources.map((source, index) => {
                    const href = httpHref(source);
                    const label = source.title ?? source.source_ref;
                    return (
                      <li key={`${source.source_type}-${source.source_ref}-${index}`}>
                        {href ? (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="text-primary underline underline-offset-4 hover:no-underline"
                          >
                            {label}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">{label}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}

            {data.findings.length > 0 ? (
              <section className="space-y-2">
                <h2 className="font-display text-lg font-semibold text-foreground">
                  Findings from this research
                </h2>
                <ul className="space-y-1 text-sm">
                  {data.findings.map((finding) => (
                    <li key={finding.research_id}>
                      <Link
                        to={`/research/${finding.research_id}`}
                        className="text-primary underline underline-offset-4 hover:no-underline"
                      >
                        {finding.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {data.item.kind === 'finding' && isOwner ? (
              <section className="space-y-2">
                <button
                  type="button"
                  className="text-primary underline"
                  onClick={async () => {
                    const request = ++sourceRequest.current;
                    setSourceError(null);
                    setSourceConversation(null);
                    try {
                      const result = await api.getFindingConversation(researchId);
                      if (request !== sourceRequest.current) return;
                      setSourceConversation({
                        id: researchId,
                        api,
                        session: sessionVersion,
                        messages: result.conversation,
                      });
                    } catch {
                      if (request !== sourceRequest.current) return;
                      setSourceError({
                        id: researchId,
                        api,
                        session: sessionVersion,
                        message: 'Source conversation excerpt is unavailable for this finding.',
                      });
                    }
                  }}
                >
                  View source conversation excerpt (only you)
                </button>
                {sourceError?.id === researchId &&
                sourceError.api === api &&
                sourceError.session === sessionVersion &&
                authStatus === 'authenticated' ? (
                  <p role="alert">{sourceError.message}</p>
                ) : null}
                {sourceConversation?.id === researchId &&
                sourceConversation.api === api &&
                sourceConversation.session === sessionVersion &&
                authStatus === 'authenticated' ? (
                  <details open>
                    <summary>Source conversation excerpt — private</summary>
                    <p className="text-sm text-muted-foreground">
                      Text retained from the bounded Ask history used for this save. Older turns may
                      be omitted. Your complete local conversation remains separate.
                    </p>
                    {sourceConversation.messages.map((message, index) => (
                      <div key={index} className="my-3 whitespace-pre-wrap">
                        <p className="font-semibold">
                          {message.role === 'user' ? 'You' : 'Almanac'}
                        </p>
                        <p>{message.text}</p>
                      </div>
                    ))}
                  </details>
                ) : null}
              </section>
            ) : null}
            {data.derived_from.length > 0 ? (
              <section className="space-y-2">
                <h2 className="font-display text-lg font-semibold text-foreground">Built from</h2>
                <ul className="space-y-1 text-sm">
                  {data.derived_from.map((source) => (
                    <li key={source.research_id}>
                      <Link
                        to={`/research/${source.research_id}`}
                        className="text-primary underline underline-offset-4 hover:no-underline"
                      >
                        {source.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {data.comments.length > 0 ? (
              <section className="space-y-2">
                <h2 className="font-display text-lg font-semibold text-foreground">Comments</h2>
                {data.comments.map((comment) => (
                  <div
                    key={comment.comment_id}
                    className="space-y-2 rounded-lg border border-border/60 bg-muted/30 p-3"
                  >
                    {comment.author ? (
                      <div className="text-xs font-medium text-muted-foreground">
                        {comment.author}
                      </div>
                    ) : null}
                    <MarkdownPrimitive body={comment.text_md} />
                    {comment.answer_md ? (
                      <div className="border-l-2 border-primary/40 pl-3">
                        <MarkdownPrimitive body={comment.answer_md} />
                      </div>
                    ) : null}
                  </div>
                ))}
              </section>
            ) : null}
          </>
        ) : null}
      </main>
    </div>
  );
}
